import { z } from 'zod';

export type SearchParams = z.infer<typeof searchSchema>;

/** The schema's boolean, or the URL's string when the root schema fails as a whole. */
export const isAgentSearch = (value: unknown) =>
  value === true || value === 'true';

const searchSchema = z.object({
  shid: z.string().optional(),
  retailer: z.string().optional(),
  orderNumber: z.string().optional(),
  order: z.string().optional(),
  email: z.email({ message: 'Invalid email address' }).optional(),
  request: z.string().optional(),
  token: z.string().optional(),
  locale: z.string().optional(),
  zipCode: z.string().optional(),
  itemId: z.string().optional(),
  resolutionId: z.string().optional(),
  claimId: z.string().optional(),
  isGift: z.string().optional(),
  complete: z.string().optional(),
  // Agent mode query param - simulates agent permissions when set to 'true'
  // In production, agent status will be determined by Salesforce authentication
  // Uses strict transform to avoid z.coerce.boolean() gotcha where "false" -> true
  agent: z.union([z.boolean(), z.string()]).transform(isAgentSearch).optional(),
});

export const validateSearch = (search: Record<string, unknown>) => {
  const result = searchSchema.safeParse(search);
  return result.data;
};

// Earlier pdp builds wrote JSON-quoted values, sometimes quoted twice, into the URL, and bookmarked
// and shared links still carry them. No real order number, email or id starts and ends with `"`.
const unquoteJsonString = (value: string): string => {
  if (!/^".*"$/s.test(value)) return value;
  try {
    const parsed: unknown = JSON.parse(value);
    return typeof parsed === 'string' ? unquoteJsonString(parsed) : value;
  } catch {
    return value;
  }
};

// pdp reads the URL raw through `getParams()` outside the router, so the router must see the same
// plain strings. TanStack's default parser turns numeric-looking values into numbers, so its
// serializer wraps a string such as an order number in JSON quotes to keep it a string; a raw read
// keeps those quotes, and passing the value back to the router quotes it again.
export const parseSearch = (searchStr: string): Record<string, string> =>
  Object.fromEntries(
    [...new URLSearchParams(searchStr)].map(([key, value]) => [
      key,
      unquoteJsonString(value),
    ]),
  );

export const stringifySearch = (
  search: Record<string, string | number | boolean | null | undefined>,
) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const getParams = () =>
  validateSearch(parseSearch(window.location.search));
export const getCountryCode = () => getParams()?.locale?.split('_')[1] ?? 'US';
